export type PokerAudioPresetId =
    | 'themeFx2'
    | 'themeFx1'
    | 'classic'
    | 'presetsClassic'
    | 'modern';

export type PokerSoundKey =
    | 'allIn'
    | 'bet'
    | 'beep'
    | 'call'
    | 'check'
    | 'fold'
    | 'raise'
    | 'showdown'
    | 'showdownCards'
    | 'shuffle'
    | 'yourTurn';

export interface CommunityCardPlacementSound {
    file: string;
    offsetSeconds: number;
    durationSeconds: number;
}

export interface PokerAudioPreset {
    id: PokerAudioPresetId;
    label: string;
    description: string;
    sounds: Record<PokerSoundKey, string>;
    communityCardPlacement: CommunityCardPlacementSound;
}

const DEFAULT_SOUNDS: Record<PokerSoundKey, string> = {
    allIn: 'all-in.mp3',
    bet: 'bet.mp3',
    beep: 'beep.mp3',
    call: 'call.mp3',
    check: 'check.mp3',
    fold: 'fold.mp3',
    raise: 'raise.mp3',
    showdown: 'showdown.mp3',
    showdownCards: 'reveal-hole-cards.mp3',
    shuffle: 'shuffle.mp3',
    yourTurn: 'your-turn.mp3'
};

const CARD_PLACEMENT_FROM_CARD_SAMPLE: CommunityCardPlacementSound = {
    file: 'card.mp3',
    offsetSeconds: 0.48,
    durationSeconds: 0.28
};

const createPresetPackSounds = (folder: string): Record<PokerSoundKey, string> => ({
    allIn: `presets/${folder}/all-in.mp3`,
    bet: `presets/${folder}/bet.mp3`,
    beep: `presets/${folder}/beep.mp3`,
    call: `presets/${folder}/call.mp3`,
    check: `presets/${folder}/check.mp3`,
    fold: `presets/${folder}/fold.mp3`,
    raise: `presets/${folder}/raise.mp3`,
    showdown: `presets/${folder}/showdown.mp3`,
    showdownCards: `presets/${folder}/reveal-hole-cards.mp3`,
    shuffle: `presets/${folder}/shuffle.mp3`,
    yourTurn: `presets/${folder}/your-turn.mp3`
});

const createPresetPackCardPlacement = (folder: string): CommunityCardPlacementSound => ({
    file: `presets/${folder}/card.mp3`,
    offsetSeconds: 0,
    durationSeconds: 0.35
});

export const POKER_AUDIO_PRESETS: readonly PokerAudioPreset[] = [
    {
        id: 'themeFx2',
        label: 'Theme FX2',
        description: 'Poker Sound FX theme 2 sample pack.',
        sounds: createPresetPackSounds('theme-fx2'),
        communityCardPlacement: createPresetPackCardPlacement('theme-fx2')
    },
    {
        id: 'themeFx1',
        label: 'Theme FX1',
        description: 'Poker Sound FX theme 1 sample pack.',
        sounds: createPresetPackSounds('theme-fx1'),
        communityCardPlacement: createPresetPackCardPlacement('theme-fx1')
    },
    {
        id: 'classic',
        label: 'Classic',
        description: 'Current PokerSolutions table sounds.',
        sounds: DEFAULT_SOUNDS,
        communityCardPlacement: CARD_PLACEMENT_FROM_CARD_SAMPLE
    },
    {
        id: 'presetsClassic',
        label: 'Classic Alternative',
        description: 'Alternative classic sample pack.',
        sounds: createPresetPackSounds('classic'),
        communityCardPlacement: createPresetPackCardPlacement('classic')
    },
    {
        id: 'modern',
        label: 'Modern',
        description: 'Coin-forward casino sounds from Mixkit.',
        sounds: createPresetPackSounds('modern'),
        communityCardPlacement: createPresetPackCardPlacement('modern')
    }
];

export const isPokerAudioPresetId = (value: string | null): value is PokerAudioPresetId => {
    return POKER_AUDIO_PRESETS.some((preset) => preset.id === value);
};

export const getPokerAudioFilesToPreload = (): string[] => {
    const files = POKER_AUDIO_PRESETS.flatMap((preset) => [
        ...Object.values(preset.sounds),
        preset.communityCardPlacement.file
    ]);

    return Array.from(new Set(files));
};
